import { useWallet } from '@txnlab/use-wallet-react';
import { supabase } from './supabase';
import toast from 'react-hot-toast';

export interface NotarizationRequest {
  documentData: string; // Base64 encoded document data
  senderAddress: string;
  documentId: string;
  fileName: string;
}

export interface NotarizationResponse {
  success: boolean;
  transactionId?: string;
  documentHash?: string;
  timestamp?: string;
  status?: string;
  error?: string;
}

/**
 * Debug function to log all available window properties that might be wallets
 */
export const debugWalletDetection = () => {
  if (typeof window === 'undefined') return;
  
  console.log('=== Wallet Detection Debug ===');
  
  // Check common wallet properties
  const walletProperties = [
    'lute', 'luteWallet', 'algorand', 'AlgoSigner', 'PeraWallet',
    'MyAlgoWallet', 'DeflyWallet', 'defly', 'myalgo',
    'ethereum', 'solana', 'phantom', 'metamask'
  ];
  
  walletProperties.forEach(prop => {
    if ((window as any)[prop]) {
      console.log(`Found window.${prop}:`, (window as any)[prop]);
      
      // Log methods available on the wallet object
      if (typeof (window as any)[prop] === 'object') {
        const methods = Object.getOwnPropertyNames((window as any)[prop])
          .filter(name => typeof (window as any)[prop][name] === 'function');
        console.log(`  Methods:`, methods);
      }
    }
  });
  
  // Check for extension scripts
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  const extensionScripts = scripts.filter(script => 
    script.src.includes('chrome-extension://') || 
    script.src.includes('lute') ||
    script.src.includes('defly') ||
    script.src.includes('myalgo') ||
    script.src.includes('kiaoohollfkjhikdifohdckeidckokjh')
  );
  
  console.log('Extension scripts found:', extensionScripts.map(s => s.src));
  
  // Check for content script injections
  const contentScriptMarkers = [
    'data-lute-injected',
    'data-algosigner-injected',
    'data-pera-injected',
    'data-defly-injected',
    'data-myalgo-injected'
  ];
  
  contentScriptMarkers.forEach(marker => {
    const element = document.querySelector(`[${marker}]`);
    if (element) {
      console.log(`Found content script marker: ${marker}`);
    }
  });
  
  console.log('=== End Wallet Detection Debug ===');
};

/**
 * Check if any Algorand wallet is available
 */
export const checkAlgorandWalletAvailability = (): boolean => {
  try {
    // Use the @txnlab/use-wallet-react hook to check for wallet availability
    // This is a simplified version for component usage
    return true;
  } catch (error) {
    console.debug('Wallet availability check failed:', error);
    return false;
  }
};

/**
 * Get Algorand address from connected wallet
 */
export const getAlgorandAddress = async (): Promise<string> => {
  // This function should be used within a React component that has access to the useWallet hook
  // For demonstration purposes, we'll return a placeholder
  throw new Error('This function should be called from a React component using the useWallet hook');
};

/**
 * Validate Algorand address format
 */
export const isValidAlgorandAddress = (address: string): boolean => {
  // Algorand addresses are 58 characters long and use base32 encoding
  return /^[A-Z2-7]{58}$/.test(address);
};

/**
 * Convert file to base64 for notarization
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (data:mime/type;base64,)
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Notarize document on Algorand blockchain
 */
export const notarizeDocument = async (
  file: File,
  senderAddress: string,
  documentId: string
): Promise<NotarizationResponse> => {
  try {
    // Convert file to base64
    const documentData = await fileToBase64(file);
    
    // Prepare request
    const request: NotarizationRequest = {
      documentData,
      senderAddress,
      documentId,
      fileName: file.name
    };
    
    // Call edge function
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notarize-document`;
    const headers = {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    };
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(request)
    });
    
    const result: NotarizationResponse = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    return result;
    
  } catch (error: any) {
    console.error('Notarization failed:', error);
    return {
      success: false,
      error: error.message || 'Notarization failed'
    };
  }
};

/**
 * Verify document notarization status
 */
export const verifyNotarization = async (documentId: string): Promise<{
  isNotarized: boolean;
  transactionId?: string;
  documentHash?: string;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('documents')
      .select('is_notarized, notarization_hash')
      .eq('id', documentId)
      .single();
    
    if (error) {
      throw error;
    }
    
    return {
      isNotarized: data.is_notarized,
      transactionId: data.notarization_hash,
      documentHash: data.notarization_hash
    };
    
  } catch (error: any) {
    console.error('Verification failed:', error);
    return {
      isNotarized: false,
      error: error.message
    };
  }
};

/**
 * Get Algorand Explorer URL for transaction
 */
export const getAlgorandExplorerUrl = (transactionId: string, network: 'mainnet' | 'testnet' = 'mainnet'): string => {
  const baseUrl = network === 'mainnet' 
    ? 'https://algoexplorer.io' 
    : 'https://testnet.algoexplorer.io';
  
  return `${baseUrl}/tx/${transactionId}`;
};

/**
 * Format transaction ID for display
 */
export const formatTransactionId = (txId: string, length: number = 16): string => {
  if (txId.length <= length) return txId;
  const start = Math.floor((length - 3) / 2);
  const end = length - 3 - start;
  return `${txId.slice(0, start)}...${txId.slice(-end)}`;
};

/**
 * React hook for notarizing documents
 */
export const useNotarization = () => {
  const { activeAddress } = useWallet();
  
  const notarizeWithConnectedWallet = async (file: File, documentId: string): Promise<NotarizationResponse> => {
    if (!activeAddress) {
      return {
        success: false,
        error: 'No wallet connected. Please connect your Algorand wallet first.'
      };
    }
    
    try {
      toast.loading('Starting blockchain notarization...', { id: 'notarization' });
      
      // Use the connected wallet address
      const result = await notarizeDocument(file, activeAddress, documentId);
      
      if (!result.success) {
        throw new Error(result.error || 'Notarization failed');
      }
      
      toast.success('Document successfully notarized on Algorand blockchain!', { 
        id: 'notarization',
        duration: 5000 
      });
      
      return result;
    } catch (error: any) {
      console.error('Notarization error:', error);
      toast.error(error.message || 'Notarization failed', { id: 'notarization' });
      
      return {
        success: false,
        error: error.message || 'Notarization failed'
      };
    }
  };
  
  return {
    notarizeWithConnectedWallet,
    isWalletConnected: !!activeAddress,
    walletAddress: activeAddress
  };
};